import React from 'react';
import { useProject } from '../../context/ProjectContext';
import { formatWan, formatMoney, formatPct } from '../../utils/format';
import PageHeader from '../../components/PageHeader';
import MetricCard from '../../components/MetricCard';
import AnimatedContent from '../../components/react-bits/AnimatedContent/AnimatedContent';
import './ProductBasicInfo.css';
import DynamicUpdateNotice from '../../components/DynamicUpdateNotice';

// 产品基础信息：原「产品概览 + AI学习流程」两页合并重组
// 视觉层级：Hero（项目是什么/现在要解决什么）→ 核心指标 → 两栏信息 → 学习输入 → 数据依据 → 策略结论
const ProductBasicInfo: React.FC = () => {
  const { currentProject: project } = useProject();
  const opt = project.optimization;

  const learningInputs = [
    { label: '产品资料', desc: '产品档案 / 条款口径' },
    { label: 'Business Brief', desc: '业务目标 / 禁止方向' },
    { label: '爆款/历史素材', desc: `已学习 ${project.learningWorkflow.materialLearning.videoCount} 条头部素材` },
    { label: '投放数据', desc: opt.overview.periodLabel },
  ];

  const totalMaterials = opt.monthly.reduce((sum, m) => sum + (m.materialCount ?? 0), 0);
  const mainTypeGroup = opt.dimensions.find(d => d.group)?.group;
  const mainTypes = opt.dimensions.filter(d => d.group === mainTypeGroup);

  return (
    <div className="basic-info-page">
      {/* 一级页面头 */}
      <PageHeader title="产品基础信息" subtitle="AI学习的输入是什么——项目档案、业务目标与当前打法的总览。" />

      {/* Hero：项目是什么 + 现在要解决什么（左基础信息三行 / 右当前打法置顶） */}
      <section className="hero-card">
        <div className="hero-main">
          <div className="hero-title-row">
            <h3 className="hero-name">{project.name}</h3>
            <span className="status-badge info">{project.category}</span>
            <span className="status-badge neutral">{project.initType === 'DATA_COLD_START' ? '冷启动' : '历史数据'}</span>
          </div>
          <div className="hero-columns">
            <div className="hero-meta-list">
              <div className="hero-meta"><span className="hero-meta-label">当前阶段</span><span className="hero-meta-value">{project.currentStage}</span></div>
              <div className="hero-meta"><span className="hero-meta-label">目标媒体</span><span className="hero-meta-value">{project.targetMedia}</span></div>
              <div className="hero-meta"><span className="hero-meta-label">业务目标</span><span className="hero-meta-value">{project.businessObjective}</span></div>
            </div>
            <div className="hero-strategy">
              <span className="hero-strategy-label">当前打法</span>
              <p className="hero-strategy-text">{project.productUnderstanding.currentStrategy}</p>
            </div>
          </div>
        </div>
        <DynamicUpdateNotice className="overview-update-notice" />
      </section>

      {/* 核心指标：4 个一级 KPI（驾驶舱主指标） */}
      <AnimatedContent direction="vertical" distance={12} duration={0.25}>
        <section className="kpi-primary">
          <MetricCard label="累计消耗" value={formatWan(opt.overview.totalSpend)} hint={opt.overview.periodLabel} />
          <MetricCard label={opt.overview.conversionLabel === '激活成本' ? '累计激活' : '累计转化'} value={opt.overview.conversions.toLocaleString()} hint={`${opt.overview.conversionLabel} ${formatMoney(opt.overview.costPerConversion)}`} />
          <MetricCard label={opt.overview.conversionLabel === '激活成本' ? '激活成本' : '转化成本'} value={formatMoney(opt.overview.costPerConversion)} hint="全周期加权" />
          <MetricCard label="平均点击率" value={formatPct(opt.overview.ctrPct)} hint="全周期加权" />
        </section>
      </AnimatedContent>

      {/* 二级指标条 */}
      <section className="kpi-secondary">
        {opt.overview.cvrPct !== undefined && (
          <span className="kpi-secondary-item"><span>平均转化率</span><b>{formatPct(opt.overview.cvrPct)}</b></span>
        )}
        {opt.overview.accountCount !== undefined && (
          <span className="kpi-secondary-item"><span>在投账户</span><b>{opt.overview.accountCount}</b><i>脱敏后口径</i></span>
        )}
        {totalMaterials > 0 && (
          <span className="kpi-secondary-item"><span>累计上新素材</span><b>{totalMaterials}</b><i>全周期推演口径</i></span>
        )}
      </section>

      {/* 两栏信息区 */}
      <div className="basic-grid">
        {/* B. 产品核心认知 */}
        <section className="surface-card info-block">
          <h3 className="section-title">产品核心认知</h3>
          <div className="info-group">
            <label className="label">核心卖点</label>
            <ul className="value-list">
              {project.productUnderstanding.coreSellingPoints.map((point, index) => (
                <li key={index}>{point}</li>
              ))}
            </ul>
          </div>
          <div className="info-row">
            <span className="label">核心人群</span>
            <span className="value">{project.productUnderstanding.targetAudience}</span>
          </div>
          <div className="info-group">
            <label className="label">主要需求</label>
            <ul className="value-list">
              {project.productUnderstanding.mainScenarios.map((scene, index) => (
                <li key={index}>{scene}</li>
              ))}
            </ul>
          </div>
          <div className="info-group">
            <label className="label">购买/决策链路</label>
            <ul className="value-list">
              {project.productUnderstanding.decisionPath.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ul>
          </div>
          <div className="info-group">
            <label className="label">表达限制</label>
            <ul className="value-list limited-list">
              {project.productSkill.complianceRules.map((rule, index) => (
                <li key={index}>{rule}</li>
              ))}
            </ul>
          </div>
        </section>

        <div className="info-col">
          {/* C. Business Brief */}
          <section className="surface-card info-block">
            <h3 className="section-title">Business Brief</h3>
            <div className="info-row">
              <span className="label">当前业务目标</span>
              <span className="value">{project.learningWorkflow.brief.businessObjective}</span>
            </div>
            <div className="info-row">
              <span className="label">当前素材任务</span>
              <span className="value">{project.learningWorkflow.brief.currentProblems || '—'}</span>
            </div>
            <div className="info-row">
              <span className="label">重点测试方向</span>
              <span className="value">{project.learningWorkflow.brief.specialRequirements || '—'}</span>
            </div>
            <div className="info-row">
              <span className="label">成本/量目标</span>
              <span className="value">{[project.learningWorkflow.brief.costTarget, project.learningWorkflow.brief.volumeTarget].filter(Boolean).join(' · ') || '—'}</span>
            </div>
            <div className="info-row">
              <span className="label">禁止方向</span>
              <span className="value">{project.learningWorkflow.brief.forbiddenDirections || '无'}</span>
            </div>
          </section>

          {/* D. AI学习输入（流程亮点） */}
          <section className="surface-card info-block flow-block">
            <h3 className="section-title">AI学习输入</h3>
            <div className="flow-inputs">
              {learningInputs.map((input, index) => (
                <div className="flow-chip" key={index}>
                  <span className="flow-chip-title">{input.label}</span>
                  <span className="flow-chip-desc">{input.desc}</span>
                </div>
              ))}
            </div>
            <div className="flow-connector" aria-hidden="true">
              <span className="flow-line" />
              <span className="flow-node">AI 理解</span>
              <span className="flow-line" />
              <span className="flow-node flow-node-strong">形成项目认知（AI拆解 / AI策略共用）</span>
            </div>
          </section>
        </div>
      </div>

      {/* E. 历史素材 / 数据依据 */}
      <section className="surface-card data-block">
        <div className="data-block-header">
          <h3 className="section-title">历史素材 / 数据依据</h3>
          <span className="data-period">{opt.overview.periodLabel} · {opt.overview.platform}</span>
        </div>

        {mainTypes.length > 0 && mainTypeGroup && (
          <p className="material-types-line">
            主要投放构成（{mainTypeGroup}）：{mainTypes.map(d => `${d.name}${d.spendSharePct !== undefined ? ` ${d.spendSharePct}%` : ''}`).join(' · ')}
          </p>
        )}

        <div className="monthly-block">
          <h4>月度趋势</h4>
          <table className="data-table">
            <thead>
              <tr>
                <th>月份</th>
                <th className="num">消耗</th>
                <th className="num">{opt.overview.conversionLabel === '激活成本' ? '激活数' : '转化数'}</th>
                <th className="num">{opt.overview.conversionLabel}</th>
                {opt.monthly[0].ctrPct !== undefined && <th className="num">点击率</th>}
                {opt.monthly[0].retentionPct !== undefined && <th className="num">次日留存</th>}
              </tr>
            </thead>
            <tbody>
              {opt.monthly.map((row, index) => (
                <tr key={index}>
                  <td>{row.month}</td>
                  <td className="num">{formatWan(row.spend)}</td>
                  <td className="num">{row.conversions.toLocaleString()}</td>
                  <td className="num">{formatMoney(row.costPerConversion)}</td>
                  {row.ctrPct !== undefined && <td className="num">{formatPct(row.ctrPct)}</td>}
                  {opt.monthly[0].retentionPct !== undefined && <td className="num">{formatPct(row.retentionPct)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="data-note">{opt.dataSourceNote}</p>
      </section>

      {/* F. 当前策略摘要 */}
      <section className="surface-card strategy-block">
        <h3 className="section-title">当前策略摘要</h3>
        <ol className="strategy-list">
          {project.learningWorkflow.strategySummary.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ol>
      </section>
    </div>
  );
};

export default ProductBasicInfo;
